import { useQueryClient } from '@tanstack/react-query';

import { FcmNotificationType } from '@/modules/notifications';
import { logger } from '@/utils/logger/logger';

import { useNotifications } from './use-notifications';

const TAG = 'useSilentQueryInvalidation:';

/**
 * Fallback mapping: FCM type → query key prefixes to invalidate.
 *
 * Note: INVOICE_READY and BILLS_READY are intentionally excluded — they use
 * a controlled page-1 reset via useFcmListRefresh instead of blanket
 * invalidation (which would re-fetch all loaded pages).
 */
const FCM_TYPE_QUERY_KEY_MAP: Partial<Record<FcmNotificationType, string[]>> = {};

/**
 * Invalidates TanStack Query caches in response to silent FCM notifications.
 *
 * Merges `queryKeys` from the backend payload with a frontend fallback mapping
 * per notification type, deduplicating before invalidation.
 *
 * Must be mounted once in `AuthenticatedTemplate`.
 */
export const useSilentQueryInvalidation = (): void => {
  const queryClient = useQueryClient();

  useNotifications(FcmNotificationType.ALL, notification => {
    if (!notification.silent) return;

    const backendKeys = notification.queryKeys;
    const fallbackKeys = FCM_TYPE_QUERY_KEY_MAP[notification.type as FcmNotificationType] ?? [];
    const allKeys = [...new Set([...backendKeys, ...fallbackKeys])];

    if (allKeys.length === 0) return;

    logger.debug(TAG, 'Invalidating query keys', { type: notification.type, keys: allKeys });

    allKeys.forEach(prefix => {
      queryClient.invalidateQueries({ queryKey: [prefix] });
    });
  });
};
